import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Lock, Trophy, Brain, Lightning, ChatCircleDots, PaperPlaneRight } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { showXpToasts } from '../lib/progression';
import { useAuth } from '../context/AuthContext';
import TeenVestLoader from '../components/TeenVestLoader';

const BUBBLE_SCENARIOS = [
  { title: 'AI Revolution Bubble', desc: 'Every company adds AI to their product. Valuations explode. But is it real?', context: 'AI stocks have surged 500% in 6 months. Companies with no AI revenue are up 300%. NVIDIA trades at 80x earnings.' },
  { title: 'Crypto Winter or Spring?', desc: 'Bitcoin hits $150K. Altcoins multiply 10x. Influencers say it will never crash.', context: 'Bitcoin is at $150,000. Dogecoin hit $2. Your neighbor just quit their job to trade crypto. Exchanges are down from traffic.' },
  { title: 'EV Mania', desc: 'Electric vehicle startups with no cars are worth billions. Tesla hits $2T.', context: 'Rivian, Lucid, and 20 new EV SPACs are trading at massive premiums. None are profitable. Tesla alone is worth more than all other automakers combined.' },
  { title: 'Meme Stock Revolution', desc: 'Reddit traders target heavily shorted stocks. GameStop hits $500 again.', context: 'Retail traders are buying GME, AMC, and BBBY. Short sellers are losing billions. Social media is flooded with diamond hand emojis.' },
  { title: 'Real Estate Frenzy', desc: 'Housing prices up 50% in one year. People buying sight-unseen. Sound familiar?', context: 'Average home price is now 12x median income. People are bidding $100K over asking. Interest rates are at 3%. Investors own 30% of new homes.' },
  { title: 'Space Industry Hype', desc: 'Space tourism launches. Space mining companies IPO at absurd valuations.', context: 'Virgin Galactic sells tickets at $450K. Asteroid mining startups raise billions. SpaceX competitors IPO with no revenue.' },
  { title: 'Biotech Breakthrough', desc: 'A new gene therapy "cures aging." Biotech stocks go parabolic.', context: 'One study shows promising results. Media reports it as a cure. Biotech ETF is up 200%. Most companies have no FDA approval.' },
  { title: 'Metaverse Gold Rush', desc: 'Virtual land sells for millions. Meta spends $10B. Is the metaverse real?', context: 'Decentraland plots sell for $2M. Meta renamed itself for this. Roblox is worth more than EA, Activision, and Take-Two combined.' },
  { title: 'Clean Energy Bubble', desc: 'Solar stocks up 400%. Government subsidies flow. But profitability?', context: 'Solar panel companies trade at 100x revenue. Government just passed a $500B green bill. Half these companies burn cash.' },
  { title: 'The Everything Bubble', desc: 'Stocks, crypto, housing, art — everything is up. What could go wrong?', context: 'S&P 500 P/E ratio is 35. Bitcoin at all-time high. NFTs selling for millions. Margin debt at record levels. Fed says inflation is transitory.' },
];

const QUESTIONS = [
  { key: 'crash_timing', label: 'When do you think this market will crash?', placeholder: 'e.g., Within 3 months, after earnings season, when the Fed raises rates...' },
  { key: 'bubble_signs', label: 'What signs tell you this is a bubble?', placeholder: 'e.g., Crazy valuations, no real revenue, everyone is buying...' },
  { key: 'invest_decision', label: 'Would you invest right now or wait? Why?', placeholder: 'e.g., I would wait because... / I would invest in X because...' },
];

const getLevelScenario = (level) => BUBBLE_SCENARIOS[(level - 1) % BUBBLE_SCENARIOS.length];

const AIBubbleMode = () => {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [screen, setScreen] = useState('levels'); // levels, scenario, analyzing, feedback
  const [level, setLevel] = useState(1);
  const [progress, setProgress] = useState({ current_level: 1, completed: [], reward_claimed: false });
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState('');
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => { fetchProgress(); }, []);

  const fetchProgress = async () => {
    try {
      const res = await api.get('/gamemode/aibubble/progress');
      setProgress(res.data.progress || { current_level: 1, completed: [], reward_claimed: false });
    } catch { /* ignore */ }
  };

  const startLevel = (lvl) => {
    setLevel(lvl);
    setAnswers({});
    setFeedback('');
    setScreen('scenario');
  };

  const handleSubmit = async () => {
    setAnalyzing(true);
    setScreen('analyzing');

    const scenario = getLevelScenario(level);

    try {
      const res = await api.post('/gamemode/aibubble/analyze', {
        scenario: `${scenario.title}: ${scenario.context}`,
        answers,
      });
      setFeedback(res.data.feedback || 'Great analysis! Keep developing your critical thinking skills.');
    } catch {
      setFeedback('Great analysis! The key to spotting bubbles is looking at fundamentals vs. hype. Keep thinking critically about what drives real value.');
    }

    // Complete the level
    try {
      const res = await api.post('/gamemode/aibubble/complete-level', { level });
      if (res.data.reward > 0) {
        toast.success(`All 50 levels complete! +$${res.data.reward.toLocaleString()} to portfolio!`);
      }
      showXpToasts(res.data.xp_result, { refreshUser: fetchUser });
      await fetchProgress();
    } catch { /* ignore */ }

    setAnalyzing(false);
    setScreen('feedback');
  };

  const allAnswered = QUESTIONS.every(q => answers[q.key]?.trim());

  // =================== LEVELS SCREEN ===================
  if (screen === 'levels') {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button onClick={() => navigate('/game-modes')} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft size={20} /> Back to Game Modes
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-5 mb-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
              <Brain size={44} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-unbounded font-bold text-white">AI Bubble Mode</h1>
              <p className="text-zinc-400">Analyze market scenarios. Get AI-powered feedback on your thinking.</p>
              <p className="text-zinc-500 text-sm mt-1">{progress.completed?.length || 0}/50 — Complete all for <span className="text-gold font-bold">+$3,000</span></p>
            </div>
          </motion.div>

          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mb-6">
            {Array.from({ length: 50 }, (_, i) => i + 1).map(lvl => {
              const done = progress.completed?.includes(lvl);
              const unlocked = lvl <= (progress.current_level || 1);
              const isCurrent = lvl === (progress.current_level || 1) && !done;
              return (
                <motion.button key={lvl} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: lvl * 0.01 }}
                  disabled={!unlocked} onClick={() => unlocked && startLevel(lvl)}
                  className={`aspect-square rounded-xl flex items-center justify-center font-bold text-sm transition-all ${
                    done ? 'bg-purple-500/20 border-2 border-purple-500 text-purple-400' :
                    isCurrent ? 'bg-purple-500/20 border-2 border-purple-500 text-purple-400 animate-pulse' :
                    unlocked ? 'bg-zinc-800 border border-zinc-700 text-white hover:border-purple-500/50' :
                    'bg-zinc-900/30 border border-zinc-800/50 text-zinc-700'
                  }`}
                  data-testid={`aibubble-level-${lvl}`}
                >
                  {done ? <CheckCircle size={18} weight="fill" /> : !unlocked ? <Lock size={14} /> : lvl}
                </motion.button>
              );
            })}
          </div>

          <div className="glass-card p-5 border-l-4 border-l-purple-500">
            <p className="text-zinc-400 text-sm">Every level: Analyze a bubble scenario, answer 3 questions, get personalized AI feedback. No wrong answers — it's about developing your thinking!</p>
          </div>
        </div>
      </div>
    );
  }

  // =================== SCENARIO + QUESTIONS ===================
  if (screen === 'scenario') {
    const scenario = getLevelScenario(level);
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-8">
          <button onClick={() => setScreen('levels')} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft size={20} /> Back to Levels
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-2 mb-2">
              <Brain size={24} weight="fill" className="text-purple-400" />
              <span className="text-purple-400 text-sm font-bold">Level {level}</span>
            </div>
            <h2 className="text-2xl font-unbounded font-bold text-white mb-2">{scenario.title}</h2>
            <p className="text-zinc-400 mb-4">{scenario.desc}</p>

            {/* Market context */}
            <div className="glass-card p-5 mb-6 border-l-4 border-l-blue-500">
              <div className="flex items-center gap-2 mb-2">
                <Lightning size={18} className="text-blue-400" />
                <span className="text-blue-400 font-bold text-sm">Market Context</span>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">{scenario.context}</p>
            </div>

            {/* Questions */}
            <div className="space-y-5 mb-6">
              {QUESTIONS.map((q, i) => (
                <motion.div key={q.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }}
                  className="glass-card p-5"
                >
                  <label className="text-white font-medium mb-3 block">{q.label}</label>
                  <textarea
                    value={answers[q.key] || ''}
                    onChange={e => setAnswers(p => ({ ...p, [q.key]: e.target.value }))}
                    placeholder={q.placeholder}
                    rows={3}
                    className="w-full bg-black/50 border border-white/10 rounded-xl p-3 text-white text-sm resize-none focus:border-purple-500/50 focus:outline-none"
                    data-testid={`aibubble-${q.key}`}
                  />
                </motion.div>
              ))}
            </div>

            <Button onClick={handleSubmit} disabled={!allAnswered}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 font-bold text-lg disabled:opacity-50"
              data-testid="aibubble-submit"
            >
              <PaperPlaneRight size={20} className="mr-2" />
              {allAnswered ? 'Submit for AI Analysis' : 'Answer all 3 questions'}
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // =================== ANALYZING ===================
  if (screen === 'analyzing') {
    return (
      <div className="min-h-screen noise-bg flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <div className="inline-block mb-6">
            <Brain size={64} weight="fill" className="text-purple-400 animate-pulse" />
          </div>
          <TeenVestLoader size={104} label="AI is analyzing your reasoning..." />
          <p className="text-zinc-600 text-sm mt-2">Comparing with real-world market patterns</p>
        </motion.div>
      </div>
    );
  }

  // =================== FEEDBACK ===================
  if (screen === 'feedback') {
    const scenario = getLevelScenario(level);
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-8">
              <div className="inline-flex p-5 rounded-full bg-purple-500/20 mb-4">
                <Trophy size={48} weight="fill" className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-unbounded font-bold text-white">Level {level} Complete!</h2>
              <p className="text-zinc-400">{scenario.title}</p>
            </div>

            {/* AI Feedback */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="glass-card p-6 mb-6 border-l-4 border-l-purple-500"
            >
              <div className="flex items-center gap-2 mb-3">
                <ChatCircleDots size={22} weight="fill" className="text-purple-400" />
                <span className="text-purple-400 font-bold">AI Feedback</span>
              </div>
              <p className="text-zinc-300 leading-relaxed" data-testid="aibubble-feedback">{feedback}</p>
            </motion.div>

            {/* Your answers recap */}
            <div className="glass-card p-5 mb-8">
              <h3 className="text-white font-bold mb-3">Your Answers</h3>
              {QUESTIONS.map(q => (
                <div key={q.key} className="mb-3 last:mb-0">
                  <p className="text-zinc-500 text-xs mb-1">{q.label}</p>
                  <p className="text-zinc-300 text-sm">{answers[q.key]}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button onClick={() => setScreen('levels')} className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700">
                Back to Levels
              </Button>
              {level < 50 && (
                <Button onClick={() => startLevel(level + 1)} className="flex-1 py-4 bg-gradient-to-r from-blue-600 to-purple-600 font-bold" data-testid="aibubble-next">
                  Next Level <ArrowRight size={18} className="ml-2" />
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default AIBubbleMode;
